import { Http } from "../Http";
import { urls } from "../Urls";
import {
  AuthTypeApi,
  ConfigurationTypeApi,
  PAConfigurationApi,
  PaginatedCptCodesApi,
  ProviderDetailApi,
  ProviderListResponseApi,
  IntegrationTypeParam,
  SavePAConfigurationRequest,
  BulkAddCptCodesResponseApi,
  ImportCptCodesResponseApi,
  CptCodeSearchResultApi,
} from "../../pages/payerIntegration/types/providerIntegrationtypes";

export class ProviderIntegrationApi {
  static getProviders() {
    return Http.get(urls.providerIntegration.providers) as Promise<
      ProviderListResponseApi
    >;
  }

  static createProvider(body: {
    providerName: string;
    providerCode: string;
    description?: string;
    status: string;
    subscriptionKey?: string;
    createdBy: string;
  }) {
    return Http.post(
      urls.providerIntegration.providers,
      body,
    ) as Promise<ProviderDetailApi>;
  }

  static getProvider(id: number) {
    return Http.get(
      urls.providerIntegration.providerById(id),
    ) as Promise<ProviderDetailApi>;
  }

  static async updateProvider(
    id: number,
    body: {
      providerName: string;
      description?: string;
      status: string;
      subscriptionKey?: string;
      updatedBy: string;
    },
  ): Promise<ProviderDetailApi> {
    const response = await Http.put(
      urls.providerIntegration.providerById(id),
      body,
    );
    return response.data as ProviderDetailApi;
  }

  /** Omit authTypeId to load the active configuration for the group. */
  static getIntegration(
    providerId: number,
    integration: IntegrationTypeParam,
    authTypeId?: number,
    configurationTypeId?: number,
  ) {
    return Http.get(
      urls.providerIntegration.paConfigurationByProvider(
        providerId,
        integration.toUpperCase(),
        authTypeId,
        configurationTypeId,
      ),
    ) as Promise<PAConfigurationApi>;
  }

  static getConfigurationTypes(integrationType: string) {
    return Http.get(
      urls.providerIntegration.configurationTypes(integrationType),
    ) as Promise<ConfigurationTypeApi[]>;
  }

  /** Single upsert: insert or update + connection test. */
  static saveIntegration(body: SavePAConfigurationRequest) {
    return Http.post(
      urls.providerIntegration.paConfiguration,
      body,
    ) as Promise<PAConfigurationApi>;
  }

  static getCptCodes(
    providerId: number,
    pageNumber = 1,
    pageSize = 100,
    search?: string,
  ) {
    return Http.get(
      urls.providerIntegration.cptCodes(
        providerId,
        pageNumber,
        pageSize,
        search,
      ),
    ) as Promise<PaginatedCptCodesApi>;
  }

  static searchCptCodes(search: string) {
    return Http.get(
      urls.providerIntegration.cptCodesSearch(search),
    ) as Promise<CptCodeSearchResultApi[]>;
  }

  static addCptCodes(
    providerId: number,
    body: { codes: string[]; codeType: string; createdBy: string },
  ) {
    return Http.post(
      urls.providerIntegration.cptCodesBase(providerId),
      body,
    ) as Promise<BulkAddCptCodesResponseApi>;
  }

  static importCptCodes(
    providerId: number,
    file: File,
    createdBy: string,
  ) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("createdBy", createdBy);
    return Http.postFormData(
      urls.providerIntegration.cptCodesImport(providerId),
      formData,
    ) as Promise<ImportCptCodesResponseApi>;
  }

  static deleteCptCode(providerId: number, cptCodeId: number, userId: string) {
    return Http.delete(
      urls.providerIntegration.cptCodeDelete(providerId, cptCodeId, userId),
    ) as Promise<boolean>;
  }

  static getAuthTypes() {
    return Http.get(
      urls.providerIntegration.authTypes,
    ) as Promise<AuthTypeApi[]>;
  }
}
